import React from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MultiSelect, MultiSelectOption } from '../../components/shared/MultiSelect';
import { ScrollArea } from '../../components/ui/scroll-area';
import { AssetListFilters } from '../../services/assetService';

// Filter fields the drawer edits — everything in AssetListFilters except the
// ones the listing page already owns directly (free-text search, status tab,
// sort, pagination).
export type AdvancedFilterState = Omit<
    AssetListFilters,
    'search' | 'tab' | 'sortBy' | 'sortDir' | 'page' | 'limit'
>;

export const EMPTY_ADVANCED_FILTERS: AdvancedFilterState = {};

/** Number of advanced filters currently applied — drives the "Filters (3)" badge. */
export function countActiveFilters(filters: AdvancedFilterState): number {
    return Object.values(filters).filter((v) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== ''
    ).length;
}

interface AssetAdvancedFiltersProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** In-progress edits, owned by the page's table-state hook. */
    draft: AdvancedFilterState;
    onDraftChange: (filters: AdvancedFilterState) => void;
    onApply: () => void;
    onClear: () => void;
    categoryOptions: MultiSelectOption[];
    brandOptions: MultiSelectOption[];
    vendorOptions: MultiSelectOption[];
    statusOptions: MultiSelectOption[];
    conditionOptions: MultiSelectOption[];
    departmentOptions: MultiSelectOption[];
    userOptions: MultiSelectOption[];
}

const ACQUISITION_OPTIONS: MultiSelectOption[] = [
    { value: 'purchased', label: 'Purchased' },
    { value: 'rented', label: 'Rented' },
];

export const AssetAdvancedFilters: React.FC<AssetAdvancedFiltersProps> = ({
    open,
    onOpenChange,
    draft,
    onDraftChange,
    onApply,
    onClear,
    categoryOptions,
    brandOptions,
    vendorOptions,
    statusOptions,
    conditionOptions,
    departmentOptions,
    userOptions,
}) => {
    // The draft lives in the page's table-state hook, which resets it to the
    // applied filters whenever the drawer opens — so editing here never
    // mutates the list until "Apply Filters" commits it.
    const set = <K extends keyof AdvancedFilterState>(key: K, value: AdvancedFilterState[K]) => {
        onDraftChange({ ...draft, [key]: value });
    };

    const idsToStrings = (ids?: number[]) => (ids ?? []).map(String);
    const stringsToIds = (values: string[]) => (values.length ? values.map(Number) : undefined);

    const handleApply = () => {
        onApply();
        onOpenChange(false);
    };

    // onClear resets both the applied filters and the draft in one go.
    const handleClearAll = () => {
        onClear();
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="sm:max-w-lg">
                <SheetHeader>
                    <SheetTitle>Advanced Filters</SheetTitle>
                </SheetHeader>

                <ScrollArea className="flex-1 min-h-0">
                    <div className="space-y-6 px-6 py-5">
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold">Classification</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-category">Category</Label>
                                <MultiSelect
                                    id="filter-category"
                                    options={categoryOptions}
                                    selected={draft.category ?? []}
                                    onChange={(v) => set('category', v.length ? v : undefined)}
                                    placeholder="Any category"
                                    searchPlaceholder="Search categories…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-brand">Brand</Label>
                                <MultiSelect
                                    id="filter-brand"
                                    options={brandOptions}
                                    selected={draft.brand ?? []}
                                    onChange={(v) => set('brand', v.length ? v : undefined)}
                                    placeholder="Any brand"
                                    searchPlaceholder="Search brands…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-model">Model</Label>
                                <Input
                                    id="filter-model"
                                    value={draft.model ?? ''}
                                    onChange={(e) => set('model', e.target.value || undefined)}
                                    placeholder="e.g. MacBook Pro"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-vendor">Vendor</Label>
                                <MultiSelect
                                    id="filter-vendor"
                                    options={vendorOptions}
                                    selected={draft.vendor ?? []}
                                    onChange={(v) => set('vendor', v.length ? v : undefined)}
                                    placeholder="Any vendor"
                                    searchPlaceholder="Search vendors…"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Status &amp; Condition</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-status">Status</Label>
                                <MultiSelect
                                    id="filter-status"
                                    options={statusOptions}
                                    selected={draft.status ?? []}
                                    onChange={(v) => set('status', (v.length ? v : undefined) as any)}
                                    placeholder="Any status"
                                    searchPlaceholder="Search statuses…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-condition">Condition</Label>
                                <MultiSelect
                                    id="filter-condition"
                                    options={conditionOptions}
                                    selected={draft.condition ?? []}
                                    onChange={(v) => set('condition', (v.length ? v : undefined) as any)}
                                    placeholder="Any condition"
                                    searchPlaceholder="Search conditions…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-acquisition">Acquisition Type</Label>
                                <MultiSelect
                                    id="filter-acquisition"
                                    options={ACQUISITION_OPTIONS}
                                    selected={draft.acquisitionType ?? []}
                                    onChange={(v) => set('acquisitionType', (v.length ? v : undefined) as any)}
                                    placeholder="Purchased or Rented"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Ownership &amp; Location</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-assignedTo">Assigned To</Label>
                                <MultiSelect
                                    id="filter-assignedTo"
                                    options={userOptions}
                                    selected={idsToStrings(draft.assignedToId)}
                                    onChange={(v) => set('assignedToId', stringsToIds(v))}
                                    placeholder="Any user"
                                    searchPlaceholder="Search users…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-businessOwner">Business Owner</Label>
                                <MultiSelect
                                    id="filter-businessOwner"
                                    options={userOptions}
                                    selected={idsToStrings(draft.businessOwnerId)}
                                    onChange={(v) => set('businessOwnerId', stringsToIds(v))}
                                    placeholder="Any owner"
                                    searchPlaceholder="Search users…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-department">Department</Label>
                                <MultiSelect
                                    id="filter-department"
                                    options={departmentOptions}
                                    selected={idsToStrings(draft.departmentId)}
                                    onChange={(v) => set('departmentId', stringsToIds(v))}
                                    placeholder="Any department"
                                    searchPlaceholder="Search departments…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-location">Location</Label>
                                <Input
                                    id="filter-location"
                                    value={draft.location ?? ''}
                                    onChange={(e) => set('location', e.target.value || undefined)}
                                    placeholder="e.g. Office Floor 3"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="filter-costCenter">Cost Center</Label>
                                <Input
                                    id="filter-costCenter"
                                    value={draft.costCenter ?? ''}
                                    onChange={(e) => set('costCenter', e.target.value || undefined)}
                                    placeholder="e.g. IT-OPS-04"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Dates</h4>
                            <div className="space-y-1.5">
                                <Label>Purchase Date Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="date"
                                        aria-label="Purchase date from"
                                        value={draft.purchaseDateFrom ?? ''}
                                        onChange={(e) => set('purchaseDateFrom', e.target.value || undefined)}
                                    />
                                    <Input
                                        type="date"
                                        aria-label="Purchase date to"
                                        value={draft.purchaseDateTo ?? ''}
                                        onChange={(e) => set('purchaseDateTo', e.target.value || undefined)}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Warranty Expiry Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="date"
                                        aria-label="Warranty expiry from"
                                        value={draft.warrantyExpiryFrom ?? ''}
                                        onChange={(e) => set('warrantyExpiryFrom', e.target.value || undefined)}
                                    />
                                    <Input
                                        type="date"
                                        aria-label="Warranty expiry to"
                                        value={draft.warrantyExpiryTo ?? ''}
                                        onChange={(e) => set('warrantyExpiryTo', e.target.value || undefined)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Financials</h4>
                            <div className="space-y-1.5">
                                <Label>Purchase Cost Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        aria-label="Purchase cost minimum"
                                        placeholder="Min"
                                        value={draft.purchaseCostMin ?? ''}
                                        onChange={(e) => set('purchaseCostMin', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        aria-label="Purchase cost maximum"
                                        placeholder="Max"
                                        value={draft.purchaseCostMax ?? ''}
                                        onChange={(e) => set('purchaseCostMax', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Monthly Rent Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        aria-label="Monthly rent minimum"
                                        placeholder="Min"
                                        value={draft.monthlyRentMin ?? ''}
                                        onChange={(e) => set('monthlyRentMin', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        aria-label="Monthly rent maximum"
                                        placeholder="Max"
                                        value={draft.monthlyRentMax ?? ''}
                                        onChange={(e) => set('monthlyRentMax', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollArea>

                <SheetFooter>
                    <Button type="button" variant="outline" onClick={handleClearAll}>
                        Clear All
                    </Button>
                    <Button type="button" onClick={handleApply}>
                        Apply Filters
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
